import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';

import apiCalls from '../../utils/apiCalls';
import utils from '../../utils/utils';

import { connect } from 'react-redux';
import DATE_FORMAT from '../../components/constants/dateFormat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import RefundForm from '../emails/refundForm';
import Modal from 'react-bootstrap/Modal';
import DELIVERY_ORDER_BATCH_STATUS from '../../components/constants/deliveryOrderBatchStatus';
import Loader from '../../components/loader';
import ErrorMessage from '../../components/errorMessage';

import {
  getDeliveryOrderBatches,
  replaceDeliveryOrderBatch,
} from '../../deliveryOrderBatches/deliveryOrderBatchesActions';
import AdminDeliveryDayViewIndex from './adminDeliveryDayViewIndex';
import { Route } from 'react-router';
import AdminDeliveryDayViewUsers from './adminDeliveryDayUsersView';
import classNames from 'classnames';
import AdminDeliveryDayViewDeliveries from './adminDeliveryDayViewDeliveries';
import ITEM_TEMPERATURE from '../../components/constants/itemTemperature';
import AdminDeliveryDayViewOrderBatchPackingMode from './adminDeliveryDayViewOrderBatchPackingMode';
import AddressModal from '../orders/addressModal';
import Helmet from 'react-helmet';

const AdminDeliveryDayView = ({
  match,
  deliveryOrderBatches,
  deliveryOrderBatchesError,
  deliveryOrderBatchesLoading,
  getDeliveryOrderBatches,
  intl,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deliveryDay, setDeliveryDay] = useState(null);
  const [modalAddress, setModalAddress] = useState(null);

  const [disabledStatuses, setDisabledStatuses] = useState([
    DELIVERY_ORDER_BATCH_STATUS.inTransit,
    DELIVERY_ORDER_BATCH_STATUS.delivered,
  ]);
  const [selectedTemperature, setSelectedTemperature] = useState(null);
  const [modalOrder, setModalOrder] = useState(null);
  const [modalOrderBatch, setModalOrderBatch] = useState(null);

  useEffect(() => {
    apiCalls
      .getDeliveryDays({
        deliveryDayId: match.params.deliveryDayId,
      })
      .then((result) => {
        if (result.data && result.data.deliveryDays.length) {
          setDeliveryDay(result.data.deliveryDays[0]);
          setLoading(false);
        }
      })
      .catch((errors) => {
        setError(utils.getErrorMessageFromResponse(errors));
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    getDeliveryOrderBatches({
      deliveryDayId: match.params.deliveryDayId,
    });
  }, []);

  const filteredOrderBatches =
    deliveryOrderBatches && deliveryOrderBatches.length
      ? deliveryOrderBatches.filter((dob) => {
          const shouldShowWithStatus = !disabledStatuses.some((ds) => ds === dob.status);
          const shouldShowWithTemperature = selectedTemperature
            ? dob.orders.some((order) => order.items.some((oi) => oi.item.temperature === selectedTemperature))
            : true;

          return shouldShowWithStatus && shouldShowWithTemperature;
        })
      : null;

  const deliveryDate = deliveryDay
    ? utils.getFormattedDateFromString(deliveryDay.deliveryDate, DATE_FORMAT.displayWithTime)
    : '';

  const deliveryOrderBatchStatusOptions = Object.keys(DELIVERY_ORDER_BATCH_STATUS).map((key) => {
    const type = DELIVERY_ORDER_BATCH_STATUS[key];
    return {
      value: type,
      label: intl.formatMessage({ id: 'delivery.day.view.table.column.status.name.' + type }),
    };
  });
  const { formatMessage } = useIntl();

  return (
    <div className="community-view__panel">
      <Helmet>
        <title>
          {formatMessage(
            { id: 'delivery.day.view.panel.title' },
            {
              deliveryDate: deliveryDate,
            }
          )}
        </title>
      </Helmet>
      <div className="community-view__panel-corner">
        <i className="fa fa-car" />
      </div>
      <h6 className="community-view__panel-title">
        <FormattedMessage
          id={'delivery.day.view.panel.title'}
          values={{
            deliveryDate: deliveryDate,
          }}
        />
      </h6>

      <Loader show={loading || deliveryOrderBatchesLoading} />
      <ErrorMessage error={error || deliveryOrderBatchesError} />

      <div className="m-b-2">
        {Object.keys(DELIVERY_ORDER_BATCH_STATUS).map((key) => {
          const status = DELIVERY_ORDER_BATCH_STATUS[key];

          const disabledIndex = disabledStatuses.findIndex((ds) => ds === status);
          const badgeClasses = classNames({
            'badge m-r-1': true,
            'badge-secondary': disabledIndex >= 0,
            'badge-primary': disabledIndex === -1,
          });

          return (
            <span
              className={badgeClasses}
              onClick={() => {
                const newDisabledStatuses = disabledStatuses.slice();
                if (disabledIndex >= 0) {
                  newDisabledStatuses.splice(disabledIndex, 1);
                } else {
                  newDisabledStatuses.push(status);
                }

                setDisabledStatuses(newDisabledStatuses);
              }}
            >
              <FormattedMessage id={`delivery.day.view.table.column.status.name.${status}`} />
            </span>
          );
        })}
      </div>

      <div className="m-b-3">
        {Object.keys(ITEM_TEMPERATURE).map((key) => {
          const temperature = ITEM_TEMPERATURE[key];

          const isSelected = selectedTemperature === temperature.name;
          const badgeClasses = classNames({
            'badge m-r-1': true,
            'badge-secondary': !isSelected,
            'badge-primary': isSelected,
          });

          return (
            <span
              className={badgeClasses}
              onClick={() => {
                if (isSelected) {
                  setSelectedTemperature(null);
                } else {
                  setSelectedTemperature(temperature.name);
                }
              }}
            >
              {temperature.icon} <FormattedMessage id={`item.form.field.temperature.name.${temperature.name}`} />
            </span>
          );
        })}
      </div>

      {deliveryDay ? (
        <>
          <Route
            exact
            path={`${match.path}`}
            render={(props) => (
              <AdminDeliveryDayViewIndex
                deliveryDay={deliveryDay}
                disabledStatuses={disabledStatuses}
                setModalOrder={setModalOrder}
                setDisabledStatuses={setDisabledStatuses}
                setModalOrderBatch={setModalOrderBatch}
                filteredOrderBatches={filteredOrderBatches}
                deliveryOrderBatchStatusOptions={deliveryOrderBatchStatusOptions}
                setModalAddress={setModalAddress}
                selectedTemperature={selectedTemperature}
                {...props}
              />
            )}
          />
          <Route
            path={`${match.path}/addresses`}
            render={(props) => (
              <AdminDeliveryDayViewUsers
                deliveryDay={deliveryDay}
                disabledStatuses={disabledStatuses}
                setDisabledStatuses={setDisabledStatuses}
                filteredOrderBatches={filteredOrderBatches}
                setModalAddress={setModalAddress}
                {...props}
              />
            )}
          />
          <Route
            path={`${match.path}/deliveries`}
            render={(props) => (
              <AdminDeliveryDayViewDeliveries
                deliveryDay={deliveryDay}
                filteredOrderBatches={filteredOrderBatches}
                setModalOrder={setModalOrder}
                setModalOrderBatch={setModalOrderBatch}
                deliveryOrderBatchStatusOptions={deliveryOrderBatchStatusOptions}
                selectedTemperature={selectedTemperature}
                {...props}
              />
            )}
          />
        </>
      ) : (
        ''
      )}

      <Modal show={!!modalOrderBatch} onHide={() => setModalOrderBatch(null)} size="xl">
        <div onClick={() => setModalOrderBatch(null)} className="b-modal-close">
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div className="b-modal">
          {modalOrderBatch ? (
            <AdminDeliveryDayViewOrderBatchPackingMode
              orderBatch={modalOrderBatch}
              closeModal={() => {
                setModalOrderBatch(null);
              }}
              setModalOrder={setModalOrder}
            />
          ) : (
            ''
          )}
        </div>
      </Modal>

      <Modal show={!!modalOrder} onHide={() => setModalOrder(null)} size="lg">
        <div onClick={() => setModalOrder(null)} className="b-modal-close">
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div className="b-modal">
          {modalOrder ? (
            <RefundForm
              order={modalOrder}
              closeModal={() => {
                setModalOrder(null);
              }}
            />
          ) : (
            ''
          )}
        </div>
      </Modal>

      <Modal show={!!modalAddress} onHide={() => setModalAddress(null)} size="lg">
        <div onClick={() => setModalAddress(null)} className="b-modal-close">
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div className="b-modal">
          {modalAddress ? (
            <AddressModal
              existingAddress={modalAddress}
              onSuccess={() => {
                getDeliveryOrderBatches({
                  deliveryDayId: match.params.deliveryDayId,
                });
                setModalAddress(null);
              }}
            />
          ) : (
            ''
          )}
        </div>
      </Modal>
    </div>
  );
};

AdminDeliveryDayView.propTypes = {
  currentUser: PropTypes.object,
};

AdminDeliveryDayView.defaultProps = {};

function mapStateToProps(state) {
  return {
    deliveryOrderBatchesLoading: state.deliveryOrderBatches.loading,
    deliveryOrderBatchesError: state.deliveryOrderBatches.error,
    deliveryOrderBatches: state.deliveryOrderBatches.deliveryOrderBatches,
    currentUser: state.currentUser.user,
  };
}

const mapDispatchToProps = {
  getDeliveryOrderBatches,
  replaceDeliveryOrderBatch,
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(AdminDeliveryDayView));
